import { Product } from '@/models/ProductTypes';

export type DiscountRange = {
  min: number;
  max: number;
  hasDiscount: boolean;
};

/**
 * Calculate the discount percentage range across a product and all its variants
 */
export function calculateProductDiscountRange(product: Product): DiscountRange {
  const discounts: number[] = [];

  // Calculate parent product discount
  if (product.productDiscountPrice && product.productDiscountPrice > 0) {
    const parentDiscount = ((product.productDiscountPrice - product.productPrice) / product.productDiscountPrice) * 100;
    if (parentDiscount > 0) {
      discounts.push(Math.floor(parentDiscount));
    }
  }

  // Calculate discounts for all variants
  if (product.productVariants?.variants) {
    product.productVariants.variants.forEach((variant) => {
      // Check sub-variants (e.g., storage options)
      if (variant.subVariants?.variants) {
        variant.subVariants.variants.forEach((subVariant) => {
          if (subVariant.variantProductDiscountPrice && subVariant.variantProductDiscountPrice > 0) {
            const variantDiscount =
              ((subVariant.variantProductDiscountPrice - subVariant.variantProductPrice) /
                subVariant.variantProductDiscountPrice) *
              100;
            if (variantDiscount > 0) {
              discounts.push(Math.floor(variantDiscount));
            }
          }
        });
      }
    });
  }

  if (discounts.length === 0) {
    return { min: 0, max: 0, hasDiscount: false };
  }

  return {
    min: Math.min(...discounts),
    max: Math.max(...discounts),
    hasDiscount: true,
  };
}

/**
 * Format the discount range for display
 * Returns null if no discount, single percentage if min === max, or "Allt að X%" for ranges
 */
export function formatDiscountRange(range: DiscountRange): string | null {
  if (!range.hasDiscount) {
    return null;
  }

  if (range.min === range.max) {
    return `${range.max}%`;
  }

  // Show "Allt að" (up to) with the maximum discount
  return `Allt að ${range.max}%`;
}
